import { faBadgePercent, faEnvelope, faSms, faStar } from '@fortawesome/pro-regular-svg-icons';
import EmailCampaignDetailComponent from 'components/EmailMarketing/EmailCampaignDetail';
import PageSidebar, { PageSidebarItem } from 'components/PageSidebar';
import { pageView } from 'googleAnalytics/GoogleAnalytics';
import { FunctionComponent, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { MarketingEmailsPath, MarketingPath, MarketingPromoCodes, MarketingReviewsPath } from 'routes/Paths';

import PrivatePage from './PrivatePage';

/**
 * Sidebar keys — same shape as `pages/Marketing.tsx#ShowTypes`. Kept as a
 * plain enum-like const to avoid the `const enum` erasure quirks that
 * different Cuddles apps handle differently. If a new Marketing surface is
 * added, update BOTH this file and `Marketing.tsx` — they're intentionally
 * duplicated for now (Option A: surgical, no shared abstraction) because the
 * item list is small and stable. If it grows or drifts, refactor to a shared
 * `MarketingSidebar` component.
 */
const SidebarKey = {
    SMSCampaigns: 'SMS',
    Reviews: 'REVIEWS',
    PromoCodes: 'PROMO_CODES',
    Emails: 'EMAILS'
} as const;

/**
 * Standalone page for the Campaign Detail view. Wraps the detail component in
 * the same `PrivatePage` + Marketing sidebar chrome that `MarketingPage` uses
 * for the campaigns list, so the sub-nav on the left stays visible when the
 * user drills into a sent campaign.
 *
 * The sidebar's selected item is always `Emails` because the detail view only
 * exists under `/marketing/emails/campaigns/:id`; clicking any other item
 * navigates away to that section via `history.push`.
 */
const EmailCampaignDetail: FunctionComponent = () => {
    const history = useHistory();

    useEffect(() => {
        document.title = 'Campaign Detail - Cuddles';
        pageView('/marketing/emails/campaigns/detail');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sidebarItems: Array<PageSidebarItem> = [
        { icon: faSms, title: 'SMS Campaigns', key: SidebarKey.SMSCampaigns },
        { icon: faStar, title: 'Reviews', key: SidebarKey.Reviews },
        { icon: faBadgePercent, title: 'Promo Codes', key: SidebarKey.PromoCodes },
        { icon: faEnvelope, title: 'Email Marketing', key: SidebarKey.Emails }
    ];

    const handleSidebarSelect = (key: string) => {
        if (key === SidebarKey.PromoCodes) {
            history.push(MarketingPromoCodes);
        } else if (key === SidebarKey.SMSCampaigns) {
            history.push(MarketingPath);
        } else if (key === SidebarKey.Reviews) {
            history.push(MarketingReviewsPath);
        } else if (key === SidebarKey.Emails) {
            history.push(MarketingEmailsPath);
        }
    };

    const sidebar = (
        <PageSidebar
            title="Marketing"
            items={sidebarItems}
            selectedKey={SidebarKey.Emails}
            onSelect={handleSidebarSelect}
        />
    );

    return (
        <PrivatePage pageSidebar={sidebar}>
            <EmailCampaignDetailComponent />
        </PrivatePage>
    );
};

export default EmailCampaignDetail;
